import { InformationCircleIcon } from "icons/outline";
import * as React from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { cn, Tooltip } from "ui";

import type { FieldOptions } from "../types";

interface FormFieldContextValue extends FieldOptions {
  helpText?: string | null;
  descriptionId: string;
  errorMessageId: string;
  testId?: string;
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(
  null
);

export const useFormFieldContext = () => {
  const context = React.useContext(FormFieldContext);
  if (!context) {
    throw new Error("useFormFieldContext must be used inside <FormField />");
  }

  return context;
};

export const FormField = ({
  children,
  className,
  testId,
  ...props
}: Omit<FormFieldContextValue, "errorMessageId" | "descriptionId"> & {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <FormFieldContext.Provider
      value={{
        ...props,
        descriptionId: `${props.name}-description`,
        errorMessageId: `${props.name}-error`,
      }}
    >
      <div className={className} data-testid={testId}>
        {children}
      </div>
    </FormFieldContext.Provider>
  );
};
FormField.displayName = "FormField";

export const FormLabel = React.forwardRef<
  HTMLLabelElement,
  React.HTMLAttributes<HTMLLabelElement>
>(({ className, children, ...props }, ref) => {
  const { label, name, required } = useFormFieldContext();

  return (
    <label
      ref={ref}
      htmlFor={name}
      className={cn(
        "mb-1 flex text-sm font-medium text-foundations-bold",
        className
      )}
      onMouseDown={(event) => {
        // prevent text selection when double clicking label & losing input focus
        // Copied from radix-ui
        if (!event.defaultPrevented && event.detail > 1) event.preventDefault();
      }}
      {...props}
    >
      {children ?? (
        <>
          {label}

          {required ? (
            <span className="ml-1 font-normal text-danger">*</span>
          ) : (
            <span className="ml-1 font-normal text-foundations-subtle">
              (Optional)
            </span>
          )}
        </>
      )}
    </label>
  );
});
FormLabel.displayName = "FormLabel";

export const FormHint = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { helpText } = useFormFieldContext();

  if (!helpText) return null;

  return (
    <Tooltip>
      <Tooltip.Trigger
        ref={ref}
        render={
          <div
            className={cn(
              "mb-1 ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-foundations-subtlest",
              className
            )}
            {...props}
          />
        }
      >
        <InformationCircleIcon className="h-4 w-4 text-foundations-subtler" />
      </Tooltip.Trigger>
      <Tooltip.Popup variant="light">{helpText}</Tooltip.Popup>
    </Tooltip>
  );
});
FormHint.displayName = "FormHint";

export const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { descriptionId } = useFormFieldContext();
  return (
    <p
      ref={ref}
      id={descriptionId}
      className={cn("mt-1 text-sm text-foundations-subtle", className)}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

export const FormErrorMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { name, label, errorMessageId } = useFormFieldContext();
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name, exact: true });
  const { error } = getFieldState(name, formState);

  if (!error) return null;

  const message = error.message?.replace("{{label}}", label);
  return (
    <p
      ref={ref}
      id={errorMessageId}
      className={cn("mt-1 text-sm text-danger", className)}
      {...props}
    >
      {message}
    </p>
  );
});
FormErrorMessage.displayName = "FormErrorMessage";
